import React, { useRef, useEffect, useState } from "react";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa6";
import { Container } from "react-bootstrap";
import generalAuditIcon from "../../../icons/GeneralAuditIcon.svg";
import { BsFillGrid1X2Fill } from "react-icons/bs";
import FindingAndIssue from "../../../icons/FindingAndIssue.svg";
import EscalationIcon from "../../../icons/EscalationIcon.svg";
import TaskManagementIcon from "../../../icons/TaskManagementIcon.svg";
import ResourceUtilIcon from "../../../icons/ResourceUtilIcon.svg";
import AuditQualityIcon from "../../../icons/AuditQualityIcon.svg";
import ActionItemIcon from "../../../icons/ActionItemIcon.svg";
import generalAuditIconActive from "../../../icons/Audit Overview.svg";
import FindingAndIssueActive from "../../../icons/Finding.svg";
import EscalationIconActive from "../../../icons/escalations.svg";
import TaskManagementIconActive from "../../../icons/Management.svg";
import ResourceUtilIconActive from "../../../icons/Resource Utilization.svg";
import AuditQualityIconActive from "../../../icons/Audit Quality Metrics.svg";
import ActionItemIconActive from "../../../icons/ActionItem.svg";
import AnalyticsIconActive from "../../../icons/analytics.svg";
import AnalyticsIconDefault from "../../../icons/analyticsDefault.svg";
import "./style.css";

const sections = [
  "General Details",
  "Questionnaire Analysis",
  "Findings & Issues",
  "Escalations",
  "Resource Utilization",
  "Audit Quality Metrics",
  "Action Items Tracking",
  "Task Management Boards",
];

interface AuditInfoProps {
  selected: string;
  auditInfoData: any;
  onClick?: (selection: string) => void;
}

const TabIconMap: Record<string, { default: string; active: string }> = {
  "General Details": {
    default: generalAuditIcon,
    active: generalAuditIconActive,
  },
  "Questionnaire Analysis": {
    default: AnalyticsIconDefault,
    active: AnalyticsIconActive,
  },
  "Findings & Issues": {
    default: FindingAndIssue,
    active: FindingAndIssueActive,
  },
  Escalations: {
    default: EscalationIcon,
    active: EscalationIconActive,
  },
  "Task Management Boards": {
    default: TaskManagementIcon,
    active: TaskManagementIconActive,
  },
  "Resource Utilization": {
    default: ResourceUtilIcon,
    active: ResourceUtilIconActive,
  },
  "Audit Quality Metrics": {
    default: AuditQualityIcon,
    active: AuditQualityIconActive,
  },
  "Action Items Tracking": {
    default: ActionItemIcon,
    active: ActionItemIconActive,
  },
};

const AuditInfo: React.FC<AuditInfoProps> = ({
  selected,
  auditInfoData,
  onClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      const el = scrollRef.current;
      if (el) {
        setShowArrows(el.scrollWidth > el.clientWidth);
        setIsAtStart(el.scrollLeft === 0);
        setIsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
      }
    };
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [auditInfoData]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <Container fluid className="audit-tabs-wrapper">
      <div className="audit-tabs-container">
        {showArrows && (
          <div className="arrow-controls mx-3">
            <FaChevronLeft
              onClick={() => scroll("left")}
              data-testid="scroll-arrow-left"
              className={` scroll-arrow ${isAtStart ? "disabled" : ""}`}
              aria-disabled={isAtStart}
            />
          </div>
        )}
        <div className="audit-tabs" data-testid="audit-tabs" ref={scrollRef}>
          {sections.map((section) => {
            const isActive = selected === section;
            return (
              <div
                key={section}
                className={`audit-tab ${isActive ? "active" : ""}`}
                onClick={() => onClick?.(section)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick?.(section);
                  }
                }}
              >
                <span
                  className={isActive ? "Icon-tab active" : "Icon-tab"}
                  data-testid="Icon-tab"
                >
                  {section === "General Details" ? (
                    <BsFillGrid1X2Fill
                      size={16}
                      className={isActive ? "" : "overview_icon"}
                    />
                  ) : (
                    <img
                      src={
                        isActive
                          ? TabIconMap[section].active
                          : TabIconMap[section].default
                      }
                      alt={section}
                      className={`tab-icon ${isActive && selected !== "Questionnaire Analysis" ? "white" : ""}`}
                    />
                  )}
                </span>
                <span className="tab-label">{section}</span>
              </div>
            );
          })}
        </div>
        {showArrows && (
          <div className="arrow-controls mx-3">
            <FaChevronRight
              onClick={() => scroll("right")}
              data-testid="scroll-arrow-right"
              className={` scroll-arrow ${isAtEnd ? "disabled" : ""}`}
              aria-disabled={isAtEnd}
            />
          </div>
        )}
      </div>
    </Container>
  );
};

export default AuditInfo;
